import { useCallback, useState } from 'react';
import { Box, LinearProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import PDFPageSelectionControls from './pdf-page-selection-controls/PDFPageSelectionControls';
import { PDFViewerTabProps } from './PDFViewerTab.props';
import { ApiCreateDocumentGenerationJob, ApiService, FileModel } from '../../../../api';
import { RetrieveDocumentGenerationJob } from '../../../../api/models/RetrieveDocumentGenerationJob';
import { useLayoutContext } from '../../../../context/LayoutContext';
import { GenerationJobParsingMode } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-parsing-mode.enum';
import { GenerationJobState } from '../../../../pages/DocumentGenerationJobs/models/enums/generation-job-state.enum';
import { useDocuments } from '../../../../services/hooks';
import { Logger } from '../../../../utilities';
import PDFViewer from '../../../FileViewer/pdf-viewer/PDFViewer';
import { PDFViewerViewMode } from '../../../FileViewer/pdf-viewer/PDFViewer.props';
import { DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS } from '../document-preview.constants';
import { PDFJobRibbon } from './pdf-job-ribbon/PDFJobRibbon';

const PDFViewerTab = ({ document, companyId }: PDFViewerTabProps) => {
  const { apiCompanyDocumentGenerationJobsNewCreate } = ApiService;
  const { currentMeasurementDate } = useLayoutContext();
  const { downloadDocumentContent } = useDocuments();
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [allowPageSelection, setAllowPageSelection] = useState(false);
  const [processingJob, setProcessingJob] = useState<RetrieveDocumentGenerationJob>();
  const [renderingJob, setRenderingJob] = useState<RetrieveDocumentGenerationJob>();
  const [genJobProcessing, setGenJobProcessing] = useState(false);
  const [renderJobProcessing, setRenderJobProcessing] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['document', document.document_reference_id, 'content'],
    enabled: !!document.document_reference_id,
    staleTime: DEFAULT_DOC_CONTENT_STALE_TIME_IN_MS,
    queryFn: () => downloadDocumentContent(document),
  });

  const handlePageToggle = (pageNumber: number) => {
    setSelectedPages(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(pageNumber)) {
        newSelected.delete(pageNumber);
      } else {
        newSelected.add(pageNumber);
      }
      return newSelected;
    });
  };

  const handleProcessDocument = useCallback(
    async (isTableExtraction: boolean = true) => {
      if (!document.file?.id || !companyId) {
        Logger.warn('Missing required data: file ID or company ID');
        return;
      }

      try {
        if (isTableExtraction) {
          setGenJobProcessing(true);
        } else {
          setRenderJobProcessing(true);
        }

        const parserOverrides = isTableExtraction
          ? [
            {
              file_type: FileModel.file_type.PDF,
              page_numbers: Array.from(selectedPages),
              pdf_parsing_mode: GenerationJobParsingMode.TABULAR,
              convert_parsed_results_to_xlsx: true,
            },
          ]
          : []; // No parser overrides for full document markdown conversion

        const procType = {
          'Cap Table': ApiCreateDocumentGenerationJob.processing_type.CAP_TABLE,
          Financials: ApiCreateDocumentGenerationJob.processing_type.FINANCIALS,
        };
        const processingType =
          procType[document.referenced_in?.[0]?.reference_object_type as keyof typeof procType] || procType.ct;

        if (!processingType) {
          Logger.error('Invalid processing type:', document.referenced_in?.[0]?.reference_object_type);
          return;
        }

        const results = await apiCompanyDocumentGenerationJobsNewCreate(companyId, {
          reference_files: [document.file.id],
          processing_type: processingType,
          desired_termination_state: GenerationJobState.PENDING_PROMPTING,
          parser_overrides: parserOverrides,
        });

        if (isTableExtraction) {
          setProcessingJob(results);
        } else {
          setRenderingJob(results);
        }
      } catch (error) {
        Logger.error(`Error ${isTableExtraction ? 'processing pages' : 'rendering document'}:`, error);
        if (isTableExtraction) {
          setGenJobProcessing(false);
        } else {
          setRenderJobProcessing(false);
        }
      }
    },
    [apiCompanyDocumentGenerationJobsNewCreate, companyId, document.file?.id, selectedPages, document.referenced_in]
  );

  const handleProcessPages = useCallback(() => handleProcessDocument(true), [handleProcessDocument]);
  const handleRenderDocument = useCallback(() => handleProcessDocument(false), [handleProcessDocument]);

  const handleClearSelection = () => {
    setSelectedPages(new Set());
  };

  const toggleSelectionMode = (enabled: boolean) => {
    setAllowPageSelection(enabled);
    if (!enabled) {
      setSelectedPages(new Set());
    }
  };

  // Map display names to reference object type codes
  const getReferenceObjectTypeCode = (displayType?: string): string => {
    const typeMap: Record<string, string> = {
      'Cap Table': 'ct',
      Financials: 'fi',
      'Fund Ownership': 'fo',
      'Custom Breakpoints': 'cb',
      'External Value': 'ev',
      'Specified Share Values': 'ssv',
      'Fund Investment Ownership Summary': 'fio',
      'Market Adjustment': 'ma',
    };

    // If it's already a code (lowercase, 2-3 chars), return it
    if (displayType && displayType.length <= 3 && displayType === displayType.toLowerCase()) {
      return displayType;
    }

    // Otherwise, map from display name to code
    return typeMap[displayType || ''] || 'ct';
  };

  const referenceObjectType = getReferenceObjectTypeCode(document.referenced_in?.[0]?.reference_object_type);

  if (isLoading) {
    return <LinearProgress />;
  }

  if (!data.content && !data.blob_url) {
    return <div>No document found: Current Document Preview size limit is 5MB</div>;
  }

  return (
    <Box>
      {/* Table Extraction Job Status Display */}
      {processingJob && currentMeasurementDate && (
        <PDFJobRibbon
          generationJob={processingJob}
          measurementDate={currentMeasurementDate}
          referenceObjectType={referenceObjectType}
        />
      )}

      {renderingJob && currentMeasurementDate && (
        <PDFJobRibbon
          generationJob={renderingJob}
          measurementDate={currentMeasurementDate}
          referenceObjectType={referenceObjectType}
        />
      )}

      <PDFPageSelectionControls
        allowPageSelection={allowPageSelection}
        selectedPages={selectedPages}
        onToggleSelectionMode={toggleSelectionMode}
        onPageToggle={handlePageToggle}
        onClearSelection={handleClearSelection}
        onProcessPages={handleProcessPages}
        onRenderDocument={handleRenderDocument}
        genJobProcessing={genJobProcessing}
        renderJobProcessing={renderJobProcessing}
        allowCitationProcessing={referenceObjectType === 'ct'}
      />

      {/* PDF Viewer */}
      <PDFViewer
        base64Content={data.content}
        src={data.blob_url}
        allowPageSelection={allowPageSelection}
        selectedPages={selectedPages}
        viewMode={allowPageSelection ? PDFViewerViewMode.THUMBNAIL : PDFViewerViewMode.DOCUMENT}
        onPageToggle={handlePageToggle}
      />
    </Box>
  );
};

export default PDFViewerTab;
